import { z } from "zod";
import { canonicalSha256 } from "./canonicalize.js";
import { protocolIdSchema, protocolTimestampSchema, protocolVersionSchema, sha256DigestSchema, UNIVERSAL_VERIFICATION_PROTOCOL, } from "./identifiers.js";
export const operationalJobKindSchema = z.enum([
    "observation",
    "capability_refresh",
    "collection",
    "replay",
    "verification",
    "talkback",
    "retry",
]);
export const operationalBlockerSchema = z.enum([
    "scope",
    "permission",
    "environment",
    "collector",
    "evaluator",
    "oracle",
    "verification",
    "delivery",
]);
export const deliveryStateSchema = z.enum(["not_started", "pending", "delivered", "failed"]);
export const operationalRecoveryScenarioSchema = z.enum([
    "credential_expired",
    "live_verifier_missing",
    "partial_upload_interrupted",
]);
export const rawEvidenceDescriptorSchema = z
    .object({
    evidenceDigest: sha256DigestSchema,
    retentionUntil: protocolTimestampSchema,
    redactionState: z.enum(["redacted", "raw_sealed"]),
    rawContentAuthorized: z.boolean(),
    discoveryMetadataDigest: sha256DigestSchema,
})
    .strict()
    .superRefine((value, context) => {
    if (value.redactionState === "raw_sealed" && !value.rawContentAuthorized) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["rawContentAuthorized"],
            message: "raw sealed evidence requires separate raw-content authorization",
        });
    }
});
const operationalJobProjectionContentSchema = z
    .object({
    protocolVersion: protocolVersionSchema,
    id: protocolIdSchema,
    kind: operationalJobKindSchema,
    tenantId: protocolIdSchema,
    environmentId: protocolIdSchema,
    workflowId: protocolIdSchema,
    attemptId: protocolIdSchema,
    idempotencyKey: protocolIdSchema,
    queuedAt: protocolTimestampSchema,
    observedAt: protocolTimestampSchema,
    queueDelayMs: z.number().int().min(0),
    collectionLatencyMs: z.number().int().min(0),
    verifierAvailable: z.boolean(),
    evidenceFreshnessMs: z.number().int().min(0),
    recurrenceMatchConfidence: z.number().min(0).max(1),
    deliveryState: deliveryStateSchema,
    rawEvidence: rawEvidenceDescriptorSchema,
    blocker: operationalBlockerSchema.nullable(),
    recoveryScenario: operationalRecoveryScenarioSchema.nullable(),
    duplicateEffectSafe: z.boolean(),
    falseCompletionPrevented: z.boolean(),
    operatorSummary: z.string().trim().min(1),
})
    .strict();
export const operationalJobProjectionSchema = operationalJobProjectionContentSchema
    .extend({ projectionDigest: sha256DigestSchema })
    .superRefine((value, context) => {
    if (value.idempotencyKey.trim() === "") {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["idempotencyKey"],
            message: "operational jobs require idempotency keys",
        });
    }
    if ((value.blocker !== null || !value.verifierAvailable || value.deliveryState !== "delivered") &&
        !value.falseCompletionPrevented) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["falseCompletionPrevented"],
            message: "blocked or incomplete jobs must not be projected as completed",
        });
    }
    if (value.deliveryState === "delivered" && value.blocker === "delivery") {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["deliveryState"],
            message: "delivery blocker cannot be delivered",
        });
    }
});
function elapsedMs(later, earlier) {
    return Math.max(0, Date.parse(later) - Date.parse(earlier));
}
function digestProjection(value, sha256) {
    return canonicalSha256(value, sha256);
}
export function createOperationalJobProjection(input) {
    const blocker = input.blocker ?? null;
    const recoveryScenario = input.recoveryScenario ?? null;
    const withoutDigest = {
        protocolVersion: UNIVERSAL_VERIFICATION_PROTOCOL,
        id: input.id,
        kind: input.kind,
        tenantId: input.tenantId,
        environmentId: input.environmentId,
        workflowId: input.workflowId,
        attemptId: input.attemptId,
        idempotencyKey: input.idempotencyKey,
        queuedAt: input.queuedAt,
        observedAt: input.observedAt,
        queueDelayMs: elapsedMs(input.observedAt, input.queuedAt),
        collectionLatencyMs: elapsedMs(input.collectedAt, input.collectionStartedAt),
        verifierAvailable: input.verifierAvailable,
        evidenceFreshnessMs: elapsedMs(input.projectedAt, input.evidenceUpdatedAt),
        recurrenceMatchConfidence: input.recurrenceMatchConfidence,
        deliveryState: input.deliveryState,
        rawEvidence: rawEvidenceDescriptorSchema.parse(input.rawEvidence),
        blocker,
        recoveryScenario,
        duplicateEffectSafe: input.duplicateEffectSafe,
        falseCompletionPrevented: blocker !== null || !input.verifierAvailable || input.deliveryState !== "delivered",
        operatorSummary: recoveryScenario && blocker
            ? `${input.kind} blocked by ${blocker}; recovery scenario ${recoveryScenario}`
            : blocker
                ? `${input.kind} blocked by ${blocker}`
                : input.deliveryState !== "delivered" && !input.verifierAvailable
                    ? `${input.kind} delivery ${input.deliveryState}; verifier unavailable`
                    : input.deliveryState !== "delivered"
                        ? `${input.kind} delivery ${input.deliveryState}`
                        : !input.verifierAvailable
                            ? `${input.kind} delivery delivered; verifier unavailable`
                            : `${input.kind} delivered with durable evidence`,
    };
    const content = operationalJobProjectionContentSchema.parse(withoutDigest);
    return operationalJobProjectionSchema.parse({
        ...content,
        projectionDigest: digestProjection(content, input.sha256),
    });
}
export const operationalDurabilityReportSchema = z
    .object({
    protocolVersion: protocolVersionSchema,
    jobKindsCovered: z.array(operationalJobKindSchema),
    blockerKindsCovered: z.array(operationalBlockerSchema),
    recoveryScenariosCovered: z.array(operationalRecoveryScenarioSchema),
    allJobsScoped: z.boolean(),
    allJobsObservable: z.boolean(),
    allRawEvidenceCustodied: z.boolean(),
    duplicateEffectsPrevented: z.boolean(),
    falseCompletionsPrevented: z.boolean(),
    projections: z.array(operationalJobProjectionSchema),
    reportDigest: sha256DigestSchema,
})
    .strict();
export function summarizeOperationalDurability(input) {
    const projections = input.projections.map((projection) => {
        const parsed = operationalJobProjectionSchema.parse(projection);
        const { projectionDigest, ...content } = parsed;
        // Rehash each input before including it in a newly content-addressed report.
        // Digest integrity alone does not prove live execution or external delivery.
        if (projectionDigest !== digestProjection(content, input.sha256)) {
            throw new Error("operational projection digest mismatch");
        }
        return parsed;
    });
    const jobKindsCovered = [...new Set(projections.map((projection) => projection.kind))].sort();
    const blockerKindsCovered = [
        ...new Set(projections
            .map((projection) => projection.blocker)
            .filter((blocker) => blocker !== null)),
    ].sort();
    const recoveryScenariosCovered = [
        ...new Set(projections
            .map((projection) => projection.recoveryScenario)
            .filter((scenario) => scenario !== null)),
    ].sort();
    const withoutDigest = {
        protocolVersion: UNIVERSAL_VERIFICATION_PROTOCOL,
        jobKindsCovered,
        blockerKindsCovered,
        recoveryScenariosCovered,
        allJobsScoped: projections.length > 0 &&
            projections.every((projection) => projection.tenantId &&
                projection.environmentId &&
                projection.workflowId &&
                projection.attemptId &&
                projection.idempotencyKey),
        allJobsObservable: projections.length > 0 &&
            projections.every((projection) => Number.isFinite(projection.queueDelayMs) &&
                Number.isFinite(projection.collectionLatencyMs) &&
                Number.isFinite(projection.evidenceFreshnessMs) &&
                projection.recurrenceMatchConfidence >= 0 &&
                projection.recurrenceMatchConfidence <= 1 &&
                projection.deliveryState.length > 0),
        allRawEvidenceCustodied: projections.length > 0 &&
            projections.every((projection) => projection.rawEvidence.evidenceDigest.startsWith("sha256:") &&
                projection.rawEvidence.discoveryMetadataDigest.startsWith("sha256:") &&
                projection.rawEvidence.retentionUntil.length > 0),
        duplicateEffectsPrevented: projections.length > 0 && projections.every((projection) => projection.duplicateEffectSafe),
        falseCompletionsPrevented: projections.length > 0 &&
            projections.every((projection) => projection.blocker === null || projection.falseCompletionPrevented),
        projections,
    };
    return operationalDurabilityReportSchema.parse({
        ...withoutDigest,
        reportDigest: canonicalSha256(withoutDigest, input.sha256),
    });
}
